import { getAndroidFlatSurfaceStyle } from '@/components/common/android-flat-surface';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import { TabbioMark } from '@/components/common/TabbioMark';
import { useTheme } from '@/hooks/useTheme';
import { GlassView } from '@/components/common/GlassView';
import { useGlassAvailable } from '@/hooks/useGlassAvailable';

interface PublicProfileHeaderLinkProps {
  primaryLabel: string;
  secondaryLabel?: string;
}

/**
 * Canonical centered public-profile address for native stack headers. Keeping
 * CV and company chrome on this one primitive gives both screens the same
 * Liquid Glass behavior and fallback geometry.
 */
export function PublicProfileHeaderLink({
  primaryLabel,
  secondaryLabel,
}: PublicProfileHeaderLinkProps) {
  const { theme } = useTheme();
  const isGlass = useGlassAvailable();

  const content = (
    <>
      <TabbioMark size={16} color={theme.text} />
      <Text style={[styles.primary, { color: theme.text }]} numberOfLines={1}>
        {primaryLabel}
      </Text>
      {secondaryLabel ? (
        <Text
          style={[styles.secondary, { color: theme.textTertiary }]}
          numberOfLines={1}
        >
          {secondaryLabel}
        </Text>
      ) : null}
    </>
  );

  if (isGlass) {
    return (
      <GlassView
        glassEffectStyle="regular"
        style={[styles.pill, styles.inner]}
      >
        {content}
      </GlassView>
    );
  }

  return (
    <View
      style={[
        styles.pill,
        styles.inner,
        styles.fallback,
        { backgroundColor: theme.surface, borderColor: theme.borderSubtle },
        getAndroidFlatSurfaceStyle('transparent'),
      ]}
    >
      {content}
    </View>
  );
}

const styles = StyleSheet.create({
  pill: {
    height: 38,
    borderRadius: 19,
    maxWidth: 240,
    overflow: 'hidden',
  },
  inner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
  },
  fallback: {
    borderWidth: StyleSheet.hairlineWidth,
  },
  primary: {
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: -0.16,
    flexShrink: 1,
  },
  secondary: {
    fontSize: 12,
    letterSpacing: -0.12,
    flexShrink: 1,
  },
});
